import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

const Support = () => {
  const { customer, token } = useCustomerAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    message: '',
    attachments: []
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (token) {
      fetchTickets();
    }
  }, [token]);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api'}/customer/support/tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api'}/customer/support/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: 'Support ticket created successfully! We\'ll get back to you soon.' });
        setShowForm(false);
        setFormData({
          subject: '',
          category: 'general',
          priority: 'medium',
          message: '',
          attachments: []
        });
        await fetchTickets();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to create support ticket' });
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'open': '#3B82F6',
      'in_progress': '#F59E0B',
      'resolved': '#10B981',
      'closed': '#6B7280'
    };
    return statusColors[status] || '#6B7280';
  };

  const getPriorityColor = (priority) => {
    const priorityColors = {
      'low': '#10B981',
      'medium': '#F59E0B',
      'high': '#EF4444',
      'urgent': '#DC2626'
    };
    return priorityColors[priority] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'open': '🔵',
      'in_progress': '🟡',
      'resolved': '✅',
      'closed': '⚫'
    };
    return icons[status] || '❓';
  };

  if (loading && tickets.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #F3F4F6',
            borderTop: '4px solid #0D7463',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading support tickets...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>
            Support Center
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: 0
          }}>
            Get help with your orders and account
          </p>
        </div>
        
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#0D7463',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New Ticket
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '24px',
          backgroundColor: message.type === 'success' ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${message.type === 'success' ? '#BBF7D0' : '#FECACA'}`,
          color: message.type === 'success' ? '#166534' : '#DC2626'
        }}>
          {message.text}
        </div>
      )}

      {/* Quick Help */}
      <div style={{
        backgroundColor: '#F0FDF4',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid #BBF7D0'
      }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#166534',
          margin: '0 0 12px 0'
        }}>
          Quick Help
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#166534',
              margin: '0 0 4px 0'
            }}>
              Order Issues
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#166534',
              margin: 0
            }}>
              Track orders, returns, refunds
            </p>
          </div>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#166534',
              margin: '0 0 4px 0'
            }}>
              Account Help
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#166534',
              margin: 0
            }}>
              Login, password, profile issues
            </p>
          </div>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#166534',
              margin: '0 0 4px 0'
            }}>
              Technical Support
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#166534',
              margin: 0
            }}>
              Website, payment, shipping issues
            </p>
          </div>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#166534',
              margin: '0 0 4px 0'
            }}>
              General Questions
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#166534',
              margin: 0
            }}>
              Policies, features, feedback
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB'
      }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 12px 0'
        }}>
          Contact Information
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 4px 0'
            }}>
              Email Support
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: 0
            }}>
              support@kwika-digits.com
            </p>
          </div>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 4px 0'
            }}>
              Response Time
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: 0
            }}>
              Within 24 hours
            </p>
          </div>
          <div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 4px 0'
            }}>
              Business Hours
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: 0
            }}>
              Mon-Fri, 9AM-6PM WAT
            </p>
          </div>
        </div>
      </div>

      {/* Support Ticket Form */}
      {showForm && (
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #E5E7EB'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 20px 0'
          }}>
            Create Support Ticket
          </h2>

          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  placeholder="Brief description of your issue"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  <option value="general">General Question</option>
                  <option value="order">Order Issue</option>
                  <option value="account">Account Problem</option>
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing Question</option>
                  <option value="refund">Refund Request</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Priority *
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="6"
                placeholder="Please provide detailed information about your issue..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: loading ? '#9CA3AF' : '#0D7463',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Support Tickets */}
      {tickets.length > 0 ? (
        <div>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0'
          }}>
            Your Support Tickets
          </h2>
          
          <div style={{
            display: 'grid',
            gap: '16px'
          }}>
            {tickets.map((ticket) => (
              <div
                key={ticket._id}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #E5E7EB'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827',
                      margin: '0 0 4px 0'
                    }}>
                      {ticket.subject}
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      margin: 0
                    }}>
                      #{ticket.ticketNumber || ticket._id.slice(-8)}
                    </p>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      padding: '4px 8px',
                      backgroundColor: `${getPriorityColor(ticket.priority)}20`,
                      borderRadius: '4px'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: getPriorityColor(ticket.priority),
                        textTransform: 'capitalize'
                      }}>
                        {ticket.priority}
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      backgroundColor: `${getStatusColor(ticket.status)}20`,
                      borderRadius: '4px'
                    }}>
                      <span style={{ fontSize: '12px' }}>
                        {getStatusIcon(ticket.status)}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: getStatusColor(ticket.status),
                        textTransform: 'capitalize'
                      }}>
                        {ticket.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  fontSize: '14px',
                  color: '#374151',
                  marginBottom: '12px'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong>Category:</strong> {ticket.category}
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong>Created:</strong> {new Date(ticket.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>Last Updated:</strong> {new Date(ticket.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                <div style={{
                  padding: '12px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  {ticket.message}
                </div>

                {ticket.replies && ticket.replies.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#111827',
                      margin: '0 0 8px 0'
                    }}>
                      Replies ({ticket.replies.length})
                    </h4>
                    {ticket.replies.map((reply, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          fontSize: '13px',
                          color: '#374151'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <span style={{ fontWeight: '500' }}>
                            {reply.from === 'customer' ? 'You' : 'Support Team'}
                          </span>
                          <span style={{ color: '#6B7280' }}>
                            {new Date(reply.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p style={{ margin: 0 }}>{reply.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          padding: '40px',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#F3F4F6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <path d="M9 12l2 2 4-4"/>
              <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"/>
              <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"/>
            </svg>
          </div>
          <h2 style={{ color: '#111827', marginBottom: '8px' }}>No support tickets</h2>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            You haven't created any support tickets yet
          </p>
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Create Your First Ticket
          </button>
        </div>
      )}
    </div>
  );
};

export default Support;
